// Check list checkbox focus: the rule a core view applies to the checkbox of a check item holding the focus. Donor
// `@lexical/list` `registerCheckList` (LexicalList.dev.mjs `handleClick`, `KEY_SPACE_COMMAND`, `KEY_ESCAPE_COMMAND`,
// `KEY_ARROW_LEFT_COMMAND`, `handleArrowUpOrDown`, `findCheckListItemSibling`, `returnFocusToRoot`), which the Lexical
// view runs itself.
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { KeyBinding } from './keymap'
import type { CorePoint, CoreState } from './state'
import { locate, pointAt, rows, type Row } from './tree-read'

/** A check item: its id, its first text row and the top-level block holding its list tree. */
export interface CheckItem { id: string; row: Row; tree: Block }

/** Every check item of `blocks` in document order (the Lexical view's `li[role="checkbox"]` order). */
export function checkItems(blocks: Blocks): CheckItem[] {
  return rows(blocks).flatMap((row) => {
    const item = row.parents.at(-1)
    const list = row.parents.at(-2)
    const tree = row.parents[0] ?? row.block
    return item?.type === 'li' && list?.type === 'list' && list.subtype === 'todo' && item.children[0] === row.block ? [{ id: item.id, row, tree }] : []
  })
}

/**
 * What a key does to the checkbox focus:
 * - `toggle`: `toggleTodo` of `item`, which keeps the focus;
 * - `focus`: the checkbox of `item` takes the focus, the caret moving to `caret` when given;
 * - `release`: the focus returns to the text, the key then running as usual when `keyMap`.
 */
export type CheckboxAction =
  | { kind: 'toggle'; item: string }
  | { kind: 'focus'; item: string; caret?: CorePoint }
  | { kind: 'release'; keyMap: boolean }

/**
 * The checkbox action of the key bound to `binding` (core `resolveKey`) while the checkbox of check item `focused` holds
 * the focus, or, with `focused` null, whether the key moves the focus onto a checkbox; null when the key is the text's.
 * Space (`activate`) toggles, Escape (`dismiss`) returns the focus, ArrowUp/ArrowDown move caret and focus to the
 * adjacent check item of the same list tree, ArrowLeft returns the focus and moves the caret; ArrowLeft with a caret
 * at the start of a check item's text focuses its checkbox.
 */
export function checkboxKey(state: CoreState, focused: string | null, binding: KeyBinding | undefined): CheckboxAction | null {
  const motion = binding && 'motion' in binding ? binding.motion : undefined
  const intent = binding && 'intent' in binding && binding.intent.type === 'moveCaret' && !binding.intent.extend ? binding.intent : undefined
  const left = intent?.unit === 'character' && intent.direction === 'backward'
  const items = checkItems(state.blocks)
  if (focused === null) {
    const selection = state.selection
    if (!left || !selection || selection.nodes?.length || selection.table) return null
    const entries = rows(state.blocks)
    const [anchor, caret] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
    if (anchor.row !== caret.row || anchor.offset !== 0 || caret.offset !== 0) return null
    const item = items.find((entry) => entry.row.block === entries[caret.row]?.block)
    return item ? { kind: 'focus', item: item.id } : null
  }
  if (motion === 'activate') return { kind: 'toggle', item: focused }
  if (motion === 'dismiss') return { kind: 'release', keyMap: false }
  if (left) return { kind: 'release', keyMap: true }
  if (intent?.unit !== 'visualLine' || (intent.direction !== 'up' && intent.direction !== 'down')) return null
  const index = items.findIndex((entry) => entry.id === focused)
  const next = items[index + (intent.direction === 'up' ? -1 : 1)]
  if (index < 0 || !next || next.tree !== items[index]?.tree) return { kind: 'release', keyMap: true }
  return { kind: 'focus', item: next.id, caret: pointAt(next.row, 0) }
}
